import { type Chain, defineChain as defineViemChain } from 'viem'
import {
    apeChain as wagmiApeChain,
    arbitrum as wagmiArbitrum,
    avalanche as wagmiAvalanche,
    base as wagmiBase,
    blast as wagmiBlast,
    bsc as wagmiBsc,
    celo as wagmiCelo,
    mainnet as wagmiMainnet,
    mantle as wagmiMantle,
    mode as wagmiMode,
    monadTestnet as wagmiMonadTestnet,
    opBNB as wagmiOpBNB,
    optimism as wagmiOptimism,
    polygon as wagmiPolygon,
    scroll as wagmiScroll,
    sepolia as wagmiSepolia,
} from 'wagmi/chains'

import { defineChain as defineAppKitChain } from '@reown/appkit/networks'
import {
    apeChain as appKitApeChain,
    arbitrum as appKitArbitrum,
    avalanche as appKitAvalanche,
    base as appKitBase,
    blast as appKitBlast,
    bsc as appKitBsc,
    celo as appKitCelo,
    mainnet as appKitMainnet,
    mantle as appKitMantle,
    mode as appKitMode,
    monadTestnet as appKitMonadTestnet,
    opBNB as appKitOpBNB,
    optimism as appKitOptimism,
    polygon as appKitPolygon,
    scroll as appKitScroll,
    sepolia as appKitSepolia,
} from '@reown/appkit/networks'

import { isFeatureEnabled } from '@src/utils/environment'

const unichainConfig = {
    id: 130,
    name: 'Unichain',
    nativeCurrency: {
        name: 'Ether',
        symbol: 'ETH',
        decimals: 18,
    },
    rpcUrls: {
        default: {
            http: ['https://unichain-mainnet.g.alchemy.com/v2/yHZvr09DRycjGM-t-j5hkUvOgfgx8gbt'],
            webSocket: [],
        },
        public: {
            http: ['https://unichain-mainnet.g.alchemy.com/v2/yHZvr09DRycjGM-t-j5hkUvOgfgx8gbt'],
            webSocket: [],
        },
    },
    blockExplorers: {
        default: { name: 'Uniscan', url: 'https://uniscan.xyz' },
    },
} as const satisfies Chain

const appKitUnichain = defineAppKitChain({
    ...unichainConfig,
    caipNetworkId: 'eip155:130',
    chainNamespace: 'eip155',
})

const wagmiUnichain = defineViemChain(unichainConfig)

// Define MegaethTestnet config
const megaethTestnetConfig = {
    id: 6342,
    name: 'MegaETH Testnet',
    nativeCurrency: {
        name: 'Ether',
        symbol: 'ETH',
        decimals: 18,
    },
    rpcUrls: {
        default: {
            http: ['https://carrot.megaeth.com/rpc'],
            webSocket: [],
        },
        public: {
            http: ['https://carrot.megaeth.com/rpc'],
            webSocket: [],
        },
    },
    blockExplorers: {
        default: { name: 'MegaETH Explorer', url: 'https://www.megaexplorer.xyz/' },
    },
} as const satisfies Chain

// Create AppKit and Wagmi versions of the chain
const appKitMegaethTestnet = defineAppKitChain({
    ...megaethTestnetConfig,
    caipNetworkId: 'eip155:6342',
    chainNamespace: 'eip155',
})

// Use defineViemChain instead of importing directly from wagmi/chains
const wagmiMegaethTestnet = defineViemChain(megaethTestnetConfig)

const appKitChains = {
    mainnet: appKitMainnet,
    optimism: appKitOptimism,
    arbitrum: appKitArbitrum,
    bsc: appKitBsc,
    base: appKitBase,
    avalanche: appKitAvalanche,
    polygon: appKitPolygon,
    celo: appKitCelo,
    blast: appKitBlast,
    scroll: appKitScroll,
    mantle: appKitMantle,
    mode: appKitMode,
    opBNB: appKitOpBNB,
    unichain: appKitUnichain,
    monadTestnet: appKitMonadTestnet,
    sepolia: appKitSepolia,
    apechain: appKitApeChain,
    megaethTestnet: appKitMegaethTestnet,
}

const wagmiChains = {
    mainnet: wagmiMainnet,
    optimism: wagmiOptimism,
    arbitrum: wagmiArbitrum,
    bsc: wagmiBsc,
    base: wagmiBase,
    avalanche: wagmiAvalanche,
    polygon: wagmiPolygon,
    celo: wagmiCelo,
    blast: wagmiBlast,
    scroll: wagmiScroll,
    mantle: wagmiMantle,
    mode: wagmiMode,
    opBNB: wagmiOpBNB,
    unichain: wagmiUnichain,
    monadTestnet: wagmiMonadTestnet,
    sepolia: wagmiSepolia,
    apechain: wagmiApeChain,
    megaethTestnet: wagmiMegaethTestnet,
}

const chains = isFeatureEnabled('USE_NEW_WALLET_PROVIDER') ? appKitChains : wagmiChains

const mainnet_chains = [
    chains.mainnet,
    chains.optimism,
    chains.arbitrum,
    chains.bsc,
    chains.base,
    chains.avalanche,
    chains.polygon,
    chains.celo,
    chains.unichain,
    ...(isFeatureEnabled('ENABLE_EXPERIMENTAL_CHAINS')
        ? [chains.blast, chains.scroll, chains.mantle, chains.mode, chains.opBNB, chains.apechain]
        : []),
] as Chain[]

export const supportedChains: Chain[] = ((): Chain[] => {
    if (isFeatureEnabled('USE_TESTNETS_ONLY')) {
        return [chains.sepolia, chains.monadTestnet, chains.megaethTestnet] as any as Chain[]
    } else {
        return isFeatureEnabled('ENABLE_TESTNETS')
            ? mainnet_chains.concat([chains.sepolia, chains.monadTestnet, chains.megaethTestnet] as any as Chain[])
            : mainnet_chains
    }
})()

if (supportedChains.length === 0) {
    throw new Error('At least one network must be configured')
}
